// src/components/Templates/TemplateSelector.jsx
/**
 * Componente para seleção de templates de WhatsApp Cloud API.
 * Inclui busca, filtros e lista de templates com preview.
 */

import React, { useEffect, useState, useCallback } from "react";
import useTemplateStore from "../../services/useTemplateStore";
import TemplateCard from "./TemplateCard";
import "./Templates.css";

const TemplateSelector = ({
  integrationId,
  onSelect,
  selectedTemplate = null,
  showFilters = true,
  showSyncButton = true,
  autoFetch = true,
}) => {
  const {
    templates,
    isLoading,
    isSyncing,
    error,
    meta,
    filters,
    setFilter,
    clearFilters,
    fetchApprovedTemplates,
    syncTemplates,
    getLastSyncFormatted,
  } = useTemplateStore();

  const [localSearch, setLocalSearch] = useState("");

  // Buscar templates ao montar ou quando integrationId mudar
  useEffect(() => {
    if (autoFetch && integrationId) {
      fetchApprovedTemplates(integrationId);
    }
  }, [integrationId, autoFetch, fetchApprovedTemplates]);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilter("search", localSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, setFilter]);

  const handleSync = useCallback(async () => {
    if (!integrationId || isSyncing) return;
    await syncTemplates(integrationId);
  }, [integrationId, isSyncing, syncTemplates]);

  const handleTemplateClick = useCallback(
    (template) => {
      if (onSelect) {
        onSelect(template);
      }
    },
    [onSelect]
  );

  // Filtragem local
  const filteredTemplates = templates.filter((template) => {
    if (filters.status && template.status !== filters.status) return false;
    if (filters.category && template.category !== filters.category) return false;
    if (filters.language && template.language !== filters.language) return false;
    if (
      filters.search &&
      !template.name.toLowerCase().includes(filters.search.toLowerCase())
    )
      return false;
    return true;
  });

  const lastSync = getLastSyncFormatted();

  // Extrair idiomas únicos dos templates
  const uniqueLanguages = [...new Set(templates.map((t) => t.language))];

  return (
    <div className="template-selector">
      {/* Header com busca e sync */}
      <div className="template-selector-header">
        <div className="template-search-wrapper">
          <input
            type="text"
            placeholder="Buscar template..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="template-search-input"
          />
          {localSearch && (
            <button
              className="template-search-clear"
              onClick={() => setLocalSearch("")}
              aria-label="Limpar busca"
            >
              x
            </button>
          )}
        </div>

        {showSyncButton && (
          <button
            className="template-sync-button"
            onClick={handleSync}
            disabled={isSyncing || !integrationId}
            title="Sincronizar templates com a Meta"
          >
            {isSyncing ? "Sincronizando..." : "Sincronizar"}
          </button>
        )}
      </div>

      {/* Filtros */}
      {showFilters && (
        <div className="template-filters">
          <select
            value={filters.category}
            onChange={(e) => setFilter("category", e.target.value)}
            className="template-filter-select"
          >
            <option value="">Todas categorias</option>
            <option value="UTILITY">Utilidade</option>
            <option value="MARKETING">Marketing</option>
            <option value="AUTHENTICATION">Autenticação</option>
          </select>

          <select
            value={filters.language}
            onChange={(e) => setFilter("language", e.target.value)}
            className="template-filter-select"
          >
            <option value="">Todos idiomas</option>
            {uniqueLanguages.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>

          {(filters.category || filters.language || filters.search) && (
            <button
              className="template-filter-clear"
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Info de sincronização */}
      {lastSync && (
        <div className="template-sync-info">
          Última sincronização: {lastSync}
        </div>
      )}

      {/* Estado de erro */}
      {error && (
        <div className="template-error">
          {error}
        </div>
      )}

      {/* Lista de templates */}
      <div className="template-list">
        {isLoading ? (
          <div className="template-loading">
            Carregando templates...
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="template-empty">
            {templates.length === 0
              ? "Nenhum template encontrado. Clique em 'Sincronizar' para buscar templates da Meta."
              : "Nenhum template corresponde aos filtros."}
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={selectedTemplate?.id === template.id}
              onClick={handleTemplateClick}
            />
          ))
        )}
      </div>

      {/* Meta info */}
      {meta.total > 0 && !isLoading && (
        <div className="template-meta-info">
          {meta.total} template{meta.total !== 1 ? "s" : ""}
          ({meta.approved} aprovado{meta.approved !== 1 ? "s" : ""})
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
